import { createFileRoute } from "@tanstack/react-router";

import { PricingPage } from "@/components/marketing/pricing-page";
import { hasPaidPrices, pricingHeadMeta } from "@/lib/plan-prices";
import { pricingFaqs } from "@/lib/pricing-faqs";
import { faqJsonLd, pageHead, softwareApplicationJsonLd } from "@/lib/seo";

export const Route = createFileRoute("/(public)/pricing")({
  /*
   * Reads the live Polar catalog on the server so the prerendered HTML carries
   * the real prices: the plan cards, the "How much does PostDaily cost?"
   * answer and the `Offer` JSON-LD. Before this the cards were fetched only in
   * the browser, and crawlers that do not run JavaScript — which is every AI
   * crawler — read a pricing page with no price on it.
   *
   * /pricing is prerendered, so this runs once per build and the HTML holds the
   * catalog as of the last deploy. The page's own query refetches in the
   * browser (the build-time data is already stale by then), so visitors always
   * see live prices; after changing a price in Polar, redeploy the web app so
   * crawlers do too.
   *
   * In the browser this returns straight away: the page's query already shows
   * skeleton cards while it loads, and awaiting the catalog here would hold the
   * whole navigation behind the router's full-screen pending spinner instead.
   */
  loader: async ({ context }) => {
    if (typeof window !== "undefined") {
      return null;
    }

    try {
      const plans = await context.queryClient.ensureQueryData(
        context.trpc.billing.listPlans.queryOptions(),
      );

      if (!hasPaidPrices(plans)) {
        // `listPlans` falls back to Free alone when Polar is unreachable. The
        // build still succeeds — one deploy without paid prices is better than
        // blocking every deploy on Polar — but it should not pass unnoticed.
        console.warn(
          "[pricing] billing.listPlans returned no paid plans; /pricing will render without paid prices until the next build.",
        );
      }

      return plans;
    } catch (cause) {
      // Same trade-off: render without the catalog (the browser query still
      // loads it) rather than fail the page.
      console.warn(
        "[pricing] billing.listPlans failed while rendering /pricing",
        cause,
      );
      return null;
    }
  },
  head: ({ loaderData }) =>
    pageHead("pricing", {
      ...pricingHeadMeta(loaderData),
      jsonLd: [
        faqJsonLd(pricingFaqs(loaderData)),
        softwareApplicationJsonLd(loaderData),
      ],
    }),
  component: PricingPage,
});
